import { useState, useEffect, useCallback } from 'react';
import { AuthMethod, AuthResult, Profile, Credentials } from '../types';
import { ProfileManager } from '../services/ProfileManager';
import { AuthenticationService, AuthStateManager } from '../services/AuthenticationService';
import { SessionMonitorService } from '../services/SessionMonitorService';

interface AuthenticationState {
  isAuthenticated: boolean;
  user: Profile | null;
  isLoading: boolean;
  error: string | null;
  guestGameCount: number;
  hasReachedFreeGameLimit: boolean;
  shouldPromptForProfile: boolean;
}

interface AuthenticationActions {
  login: (authMethod: AuthMethod, credentials?: Credentials) => Promise<AuthResult>;
  logout: () => Promise<void>;
  createProfile: (authMethod: AuthMethod, credentials: Credentials) => Promise<Profile>;
  incrementGuestGameCount: () => void;
  refreshToken: (authMethod: AuthMethod, token: string) => Promise<string>;
  clearError: () => void;
  // Email authentication methods
  signUp: (email: string, password: string, name?: string) => Promise<void>;
  signIn: (email: string, password: string, rememberMe: boolean) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  // OAuth methods
  signInWithYahoo: () => Promise<void>;
  signInWithFacebook: () => Promise<void>;
}

/**
 * Custom hook for managing authentication state and actions
 */
export function useAuthentication(): AuthenticationState & AuthenticationActions {
  const [state, setState] = useState<AuthenticationState>({
    isAuthenticated: false,
    user: null,
    isLoading: true,
    error: null,
    guestGameCount: 0,
    hasReachedFreeGameLimit: false,
    shouldPromptForProfile: false
  });

  // Initialize services
  const [profileManager] = useState(() => new ProfileManager());
  const [authService] = useState(() => new AuthenticationService(profileManager));
  const [authStateManager] = useState(() => new AuthStateManager());
  const [sessionMonitor] = useState(() => SessionMonitorService.getInstance());

  // Update state helper
  const updateState = useCallback((updates: Partial<AuthenticationState>) => {
    setState((prev: AuthenticationState) => ({ ...prev, ...updates }));
  }, []);

  // Authentication actions - define logout first to avoid forward reference
  const logout = useCallback(async (): Promise<void> => {
    try {
      updateState({ isLoading: true, error: null });

      // Stop session monitoring
      sessionMonitor.stopMonitoring();

      await profileManager.logout();
      authStateManager.clearCurrentUser();

      updateState({ isLoading: false });
    } catch (error) {
      updateState({
        error: error instanceof Error ? error.message : 'Logout failed',
        isLoading: false
      });
    }
  }, [profileManager, authStateManager, sessionMonitor, updateState]);

  // Initialize authentication state
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        updateState({ isLoading: true, error: null });

        // Initialize third-party providers
        await authService.initializeAllProviders();

        // Try to restore existing session first
        console.log('🔍 Checking for existing session...');
        const sessionResult = await authService.restoreSession();
        
        if (sessionResult.success && sessionResult.profile) {
          console.log('✅ Session restored automatically');
          authStateManager.setCurrentUser(sessionResult.profile);
          
          updateState({
            isAuthenticated: true,
            user: sessionResult.profile,
            guestGameCount: 0, // Reset guest count for authenticated users
            hasReachedFreeGameLimit: false,
            shouldPromptForProfile: false,
            isLoading: false
          });
          
          // Start session monitoring
          sessionMonitor.startMonitoring({
            onSessionExpiring: (timeRemaining) => {
              console.log(`⚠️ Session expiring in ${Math.round(timeRemaining / 1000 / 60)} minutes`);
            },
            onSessionExpired: async () => {
              console.log('❌ Session expired, logging out');
              await logout();
            },
            onTokenRefreshed: () => {
              console.log('✅ Token refreshed automatically');
            },
            onRefreshFailed: async () => {
              console.log('❌ Token refresh failed, logging out');
              await logout();
            }
          });
          
          return;
        }

        // No valid session, load guest state
        console.log('ℹ️ No valid session found, loading guest state');
        const currentProfile = profileManager.getCurrentProfile();
        const guestGameCount = profileManager.getGuestGameCount();
        const hasReachedFreeGameLimit = profileManager.hasReachedFreeGameLimit();
        const shouldPromptForProfile = profileManager.shouldPromptForProfile();

        updateState({
          isAuthenticated: false,
          user: currentProfile,
          guestGameCount,
          hasReachedFreeGameLimit,
          shouldPromptForProfile,
          isLoading: false
        });
      } catch (error) {
        console.error('Authentication initialization failed:', error);
        updateState({
          error: error instanceof Error ? error.message : 'Failed to initialize authentication',
          isLoading: false
        });
      }
    };

    initializeAuth();
  }, [profileManager, authService, authStateManager, sessionMonitor, updateState, logout]);

  // Set up authentication event listeners
  useEffect(() => {
    const handleLogin = (user: Profile) => {
      updateState({
        isAuthenticated: true,
        user,
        hasReachedFreeGameLimit: false,
        shouldPromptForProfile: false,
        error: null
      });
    };

    const handleLogout = () => {
      updateState({
        isAuthenticated: false,
        user: null,
        guestGameCount: profileManager.getGuestGameCount(),
        hasReachedFreeGameLimit: profileManager.hasReachedFreeGameLimit(),
        shouldPromptForProfile: profileManager.shouldPromptForProfile()
      });
    };

    const handleTokenRefresh = (data: { authMethod: AuthMethod; newToken: string }) => {
      console.log(`Token refreshed for ${data.authMethod}`);
      // In a real implementation, you might want to update stored tokens
    };

    authStateManager.addEventListener('login', handleLogin);
    authStateManager.addEventListener('logout', handleLogout);
    authStateManager.addEventListener('tokenRefresh', handleTokenRefresh);

    return () => {
      authStateManager.removeEventListener('login', handleLogin);
      authStateManager.removeEventListener('logout', handleLogout);
      authStateManager.removeEventListener('tokenRefresh', handleTokenRefresh);
    };
  }, [authStateManager, profileManager, updateState]);

  // Authentication actions (continued)
  const login = useCallback(async (authMethod: AuthMethod, credentials?: Credentials): Promise<AuthResult> => {
    try {
      updateState({ isLoading: true, error: null });

      let result: AuthResult;

      // Handle different authentication methods
      switch (authMethod) {
        case AuthMethod.GOOGLE:
          result = await authService.authenticateWithGoogle();
          break;
        case AuthMethod.FACEBOOK:
          result = await authService.authenticateWithFacebook();
          break;
        case AuthMethod.APPLE:
          result = await authService.authenticateWithApple();
          break;
        case AuthMethod.YAHOO:
          result = await authService.authenticateWithYahoo();
          break;
        case AuthMethod.CUSTOM:
          if (!credentials) {
            throw new Error('Credentials required for custom authentication');
          }
          result = await profileManager.authenticateUser(authMethod, credentials);
          break;
        case AuthMethod.GUEST:
          result = { success: true }; // Guest mode doesn't require authentication
          break;
        default:
          throw new Error(`Unsupported authentication method: ${authMethod}`);
      }

      if (result.success && result.profile) {
        authStateManager.setCurrentUser(result.profile);
        
        // Start session monitoring for authenticated users
        sessionMonitor.startMonitoring({
          onSessionExpiring: (timeRemaining) => {
            console.log(`⚠️ Session expiring in ${Math.round(timeRemaining / 1000 / 60)} minutes`);
          },
          onSessionExpired: async () => {
            console.log('❌ Session expired, logging out');
            await logout();
          },
          onTokenRefreshed: () => {
            console.log('✅ Token refreshed automatically');
          },
          onRefreshFailed: async () => {
            console.log('❌ Token refresh failed, logging out');
            await logout();
          }
        });
      }

      updateState({ isLoading: false });
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Authentication failed';
      updateState({ 
        error: errorMessage, 
        isLoading: false 
      });
      return { 
        success: false, 
        error: errorMessage 
      };
    }
  }, [authService, profileManager, authStateManager, updateState]);

  const createProfile = useCallback(async (authMethod: AuthMethod, credentials: Credentials): Promise<Profile> => {
    try {
      updateState({ isLoading: true, error: null });

      const profile = await profileManager.createProfile(authMethod, credentials);
      authStateManager.setCurrentUser(profile);

      updateState({ isLoading: false });
      return profile;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Profile creation failed';
      updateState({
        error: errorMessage,
        isLoading: false
      });
      throw error;
    }
  }, [profileManager, authStateManager, updateState]);

  const incrementGuestGameCount = useCallback((): void => {
    profileManager.incrementGuestGameCount();
    
    const guestGameCount = profileManager.getGuestGameCount();
    const hasReachedFreeGameLimit = profileManager.hasReachedFreeGameLimit();
    const shouldPromptForProfile = profileManager.shouldPromptForProfile();

    updateState({
      guestGameCount,
      hasReachedFreeGameLimit,
      shouldPromptForProfile
    });
  }, [profileManager, updateState]);

  const refreshToken = useCallback(async (authMethod: AuthMethod, token: string): Promise<string> => {
    try {
      const newToken = await authService.refreshToken(authMethod, token);
      authStateManager.notifyTokenRefresh(authMethod, newToken);
      return newToken;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Token refresh failed';
      updateState({ error: errorMessage });
      throw error;
    }
  }, [authService, authStateManager, updateState]);

  const clearError = useCallback((): void => {
    updateState({ error: null });
  }, [updateState]);

  // Email authentication methods
  const signUp = useCallback(async (email: string, password: string, name?: string): Promise<void> => {
    try {
      updateState({ isLoading: true, error: null });

      const credentials: Credentials = { email, password };
      const profile = await profileManager.createProfile(AuthMethod.CUSTOM, credentials);
      
      // Update profile name if provided
      if (name && profile) {
        profile.name = name;
        await profileManager.syncGameData(profile);
      }

      authStateManager.setCurrentUser(profile);

      // Start session monitoring
      sessionMonitor.startMonitoring({
        onSessionExpiring: (timeRemaining) => {
          console.log(`⚠️ Session expiring in ${Math.round(timeRemaining / 1000 / 60)} minutes`);
        },
        onSessionExpired: async () => {
          console.log('❌ Session expired, logging out');
          await logout();
        },
        onTokenRefreshed: () => {
          console.log('✅ Token refreshed automatically');
        },
        onRefreshFailed: async () => {
          console.log('❌ Token refresh failed, logging out');
          await logout();
        }
      });

      updateState({ isLoading: false });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Signup failed';
      updateState({
        error: errorMessage,
        isLoading: false
      });
      throw error;
    }
  }, [profileManager, authStateManager, sessionMonitor, updateState, logout]);

  const signIn = useCallback(async (email: string, password: string, rememberMe: boolean): Promise<void> => {
    try {
      updateState({ isLoading: true, error: null });

      const credentials: Credentials = { email, password };
      const result = await profileManager.authenticateUser(AuthMethod.CUSTOM, credentials);

      if (!result.success || !result.profile) {
        throw new Error(result.error || 'Login failed');
      }

      authStateManager.setCurrentUser(result.profile);

      // Start session monitoring
      sessionMonitor.startMonitoring({
        onSessionExpiring: (timeRemaining) => {
          console.log(`⚠️ Session expiring in ${Math.round(timeRemaining / 1000 / 60)} minutes`);
        },
        onSessionExpired: async () => {
          console.log('❌ Session expired, logging out');
          await logout();
        },
        onTokenRefreshed: () => {
          console.log('✅ Token refreshed automatically');
        },
        onRefreshFailed: async () => {
          console.log('❌ Token refresh failed, logging out');
          await logout();
        }
      });

      updateState({ isLoading: false });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      updateState({
        error: errorMessage,
        isLoading: false
      });
      throw error;
    }
  }, [profileManager, authStateManager, sessionMonitor, updateState, logout]);

  const resetPassword = useCallback(async (email: string): Promise<void> => {
    try {
      updateState({ isLoading: true, error: null });

      // In a real implementation, this would call a backend API to send reset email
      // For now, we'll simulate the request
      console.log('Password reset requested for:', email);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      // In production, you would:
      // 1. Call your backend API: await api.requestPasswordReset(email)
      // 2. Backend sends email with reset link
      // 3. User clicks link and is redirected to reset password page
      // 4. User enters new password
      // 5. Backend updates password

      updateState({ isLoading: false });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Password reset failed';
      updateState({
        error: errorMessage,
        isLoading: false
      });
      throw error;
    }
  }, [updateState]);

  const signInWithYahoo = useCallback(async (): Promise<void> => {
    try {
      updateState({ isLoading: true, error: null });

      const result = await authService.authenticateWithYahoo();

      if (!result.success || !result.profile) {
        throw new Error(result.error || 'Yahoo login failed');
      }

      authStateManager.setCurrentUser(result.profile);

      // Start session monitoring
      sessionMonitor.startMonitoring({
        onSessionExpiring: (timeRemaining) => {
          console.log(`⚠️ Session expiring in ${Math.round(timeRemaining / 1000 / 60)} minutes`);
        },
        onSessionExpired: async () => {
          console.log('❌ Session expired, logging out');
          await logout();
        },
        onTokenRefreshed: () => {
          console.log('✅ Token refreshed automatically');
        },
        onRefreshFailed: async () => {
          console.log('❌ Token refresh failed, logging out');
          await logout();
        }
      });

      updateState({ isLoading: false });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Yahoo login failed';
      updateState({
        error: errorMessage,
        isLoading: false
      });
      throw error;
    }
  }, [authService, authStateManager, sessionMonitor, updateState, logout]);

  const signInWithFacebook = useCallback(async (): Promise<void> => {
    try {
      updateState({ isLoading: true, error: null });

      const result = await authService.authenticateWithFacebook();

      if (!result.success || !result.profile) {
        throw new Error(result.error || 'Facebook login failed');
      }

      authStateManager.setCurrentUser(result.profile);

      // Start session monitoring
      sessionMonitor.startMonitoring({
        onSessionExpiring: (timeRemaining) => {
          console.log(`⚠️ Session expiring in ${Math.round(timeRemaining / 1000 / 60)} minutes`);
        },
        onSessionExpired: async () => {
          console.log('❌ Session expired, logging out');
          await logout();
        },
        onTokenRefreshed: () => {
          console.log('✅ Token refreshed automatically');
        },
        onRefreshFailed: async () => {
          console.log('❌ Token refresh failed, logging out');
          await logout();
        }
      });

      updateState({ isLoading: false });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Facebook login failed';
      updateState({
        error: errorMessage,
        isLoading: false
      });
      throw error;
    }
  }, [authService, authStateManager, sessionMonitor, updateState, logout]);

  return {
    ...state,
    login,
    logout,
    createProfile,
    incrementGuestGameCount,
    refreshToken,
    clearError,
    signUp,
    signIn,
    resetPassword,
    signInWithYahoo,
    signInWithFacebook
  };
}